let mapsScriptPromise;
const libraryPromises = new Map();

const IMPORT_LIBRARY_POLL_INTERVAL_MS = 50;
const IMPORT_LIBRARY_TIMEOUT_MS = 10000;

export const REGION_BOUNDS = {
    MelbourneEast: {south: -38.35, west: 144.75, north: -37.30, east: 146.15},
    MelbourneWest: {south: -38.35, west: 143.45, north: -37.20, east: 145.15},
    Brisbane: {south: -28.35, west: 152.55, north: -26.85, east: 153.65},
    GoldCoast: {south: -28.60, west: 152.70, north: -27.65, east: 153.70},
    Mackay: {south: -22.20, west: 148.20, north: -20.40, east: 150.00},
};

export function hasGooglePlacesKey() {
    return Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
}

function loadGoogleMapsScript() {
    if (mapsScriptPromise) return mapsScriptPromise;
    const nextPromise = new Promise((resolve, reject) => {
        if (!hasGooglePlacesKey()) {
            reject(new Error('Google Maps API key is not configured'));
            return;
        }
        if (window.google?.maps?.importLibrary) {
            resolve(window.google.maps.importLibrary.bind(window.google.maps));
            return;
        }
        let script = document.querySelector('script[data-cos-google-maps="true"]');
        const existing = Boolean(script);
        if (!script) {
            script = document.createElement('script');
            script.dataset.cosGoogleMaps = 'true';
            script.async = true;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(process.env.REACT_APP_GOOGLE_MAPS_API_KEY)}&loading=async&v=weekly`;
        }
        const startedAt = Date.now();
        let timer;
        let settled = false;
        const finish = (error) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            script.removeEventListener('error', fail);
            if (error) {
                script.remove();
                reject(error);
            } else {
                resolve(window.google.maps.importLibrary.bind(window.google.maps));
            }
        };
        const fail = () => finish(new Error('Google Maps failed to load'));
        const check = () => {
            if (window.google?.maps?.importLibrary) {
                finish();
            } else if (Date.now() - startedAt >= IMPORT_LIBRARY_TIMEOUT_MS) {
                finish(new Error('Google Maps did not finish loading'));
            } else {
                timer = window.setTimeout(check, IMPORT_LIBRARY_POLL_INTERVAL_MS);
            }
        };
        script.addEventListener('error', fail, {once: true});
        if (!existing) document.head.appendChild(script);
        check();
    });
    mapsScriptPromise = nextPromise;
    nextPromise.catch(() => {
        if (mapsScriptPromise === nextPromise) mapsScriptPromise = undefined;
    });
    return nextPromise;
}

export function loadGoogleMapsLibrary(name) {
    if (libraryPromises.has(name)) return libraryPromises.get(name);
    const promise = loadGoogleMapsScript().then(importLibrary => new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error(`Google Maps ${name} library did not finish loading`)), IMPORT_LIBRARY_TIMEOUT_MS);
        Promise.resolve().then(() => importLibrary(name)).then(value => {
            window.clearTimeout(timeout);
            resolve(value);
        }, error => {
            window.clearTimeout(timeout);
            reject(error);
        });
    }));
    libraryPromises.set(name, promise);
    promise.catch(() => {
        if (libraryPromises.get(name) === promise) libraryPromises.delete(name);
    });
    return promise;
}

export function loadGooglePlaces() {
    return loadGoogleMapsLibrary('places');
}

function componentValue(components, type, short = false) {
    const component = components?.find(value => value.types?.includes(type));
    if (!component) return '';
    return short
        ? component.shortText || component.short_name || ''
        : component.longText || component.long_name || '';
}

function coordinate(value) {
    if (typeof value === 'function') return value();
    return value;
}

export function addressFromPlace(place) {
    const components = place.addressComponents || place.address_components || [];
    const streetNumber = componentValue(components, 'street_number');
    const route = componentValue(components, 'route');
    const addressLine1 = [streetNumber, route].filter(Boolean).join(' ');
    const location = place.location || place.geometry?.location;
    return {
        formatted_address: place.formattedAddress || place.formatted_address || '',
        address_line_1: addressLine1,
        address_line_2: componentValue(components, 'subpremise'),
        suburb: componentValue(components, 'locality')
            || componentValue(components, 'postal_town')
            || componentValue(components, 'sublocality'),
        state: componentValue(components, 'administrative_area_level_1', true),
        postcode: componentValue(components, 'postal_code'),
        country_code: componentValue(components, 'country', true).toUpperCase(),
        google_place_id: place.id || place.place_id || '',
        latitude: coordinate(location?.lat),
        longitude: coordinate(location?.lng),
    };
}
